import { Link } from 'react-router-dom';
import { FaFilePdf, FaSpinner, FaCheckCircle, FaExclamationCircle, FaTrash } from 'react-icons/fa';
import { ExtractionPayload, FileEntry, FileStatus, UnderlyingReceivable } from '../../types/draweeRisk';

const formatCurrency = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
};

interface FileCardProps {
    entry: FileEntry;
    onRemove: (id: string) => void;
}

interface MainRiskDisplayProps {
    mainRisk: NonNullable<ExtractionPayload['operation_dictionary']['main_risk']>;
}

const MainRiskDisplay = ({ mainRisk }: MainRiskDisplayProps) => (
    <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
        <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Risco Principal</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <div>
                <span className="text-gray-500 dark:text-gray-400">Nome</span>
                <p className="text-gray-900 dark:text-gray-100 font-medium truncate">{mainRisk.name || '-'}</p>
            </div>
            <div>
                <span className="text-gray-500 dark:text-gray-400">Documento</span>
                <p className="text-gray-900 dark:text-gray-100 font-medium">{mainRisk.document || '-'}</p>
            </div>
        </div>
    </div>
);

const FileCard = ({ entry, onRemove }: FileCardProps) => {
    const { file, status, response, errorMessage } = entry;

    const renderStatusIcon = () => {
        if (entry.isEnriching) return <FaSpinner className="text-blue-500 animate-spin text-lg" />;
        switch (status) {
            case FileStatus.UPLOADING: return <FaSpinner className="text-blue-500 animate-spin text-lg" />;
            case FileStatus.SUCCESS: return <FaCheckCircle className="text-green-500 text-lg" />;
            case FileStatus.ERROR: return <FaExclamationCircle className="text-red-500 text-lg" />;
            default: return <FaFilePdf className="text-gray-400 text-lg" />;
        }
    };

    const renderContent = () => {
        if (status === FileStatus.UPLOADING) {
            return <p className="text-sm text-gray-500 dark:text-gray-400">Processando PDF...</p>;
        }

        if (status === FileStatus.ERROR) {
            return (
                <div className="text-sm">
                    <p className="text-red-600 dark:text-red-400">{errorMessage || 'Erro ao processar arquivo'}</p>
                    {entry.duplicateInfo && (
                        <Link to={`/operations/drawee-risk/${entry.duplicateInfo.credit_operation_id}`}
                            className="inline-block mt-1 text-blue-600 dark:text-blue-400 hover:underline text-xs">
                            Abrir operação existente (Contrato #{entry.duplicateInfo.sequencial_number})
                        </Link>
                    )}
                </div>
            );
        }

        if (status === FileStatus.SUCCESS && response?.payload) {
            const { payload } = response;
            const assignor = payload.participants?.find(p => p.participant_type === 'ASSIGNOR');
            const drawee = payload.participants?.find(p => p.participant_type === 'DRAWEE');
            const receivables = payload.operation_dictionary?.underlying_receivables;

            return (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 px-2 py-0.5 rounded">
                            {response.program}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(payload.emission_date)} → {formatDate(payload.expiration_date)}
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Cedente</span>
                            <p className="text-gray-900 dark:text-gray-100 font-medium truncate">{assignor?.name || '-'}</p>
                        </div>
                        <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Sacado/Âncora</span>
                            <p className="text-gray-900 dark:text-gray-100 font-medium truncate">{drawee?.name || '-'}</p>
                        </div>
                        <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Valor Nominal</span>
                            <p className="text-gray-900 dark:text-gray-100 font-medium">{formatCurrency(Number(payload.nominal_value))}</p>
                        </div>
                    </div>

                    {entry.isEnriching && (
                        <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded px-3 py-2">
                            <FaSpinner className="animate-spin" /> Buscando dados complementares (participantes, endereço e risco principal)...
                        </div>
                    )}

                    {payload.operation_dictionary?.main_risk && (
                        <MainRiskDisplay mainRisk={payload.operation_dictionary.main_risk} />
                    )}

                    {entry.unresolvedPersons && entry.unresolvedPersons.length > 0 && (
                        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded px-3 py-2 text-xs">
                            <p className="font-medium text-amber-800 dark:text-amber-300">Participante(s) não encontrado(s) no cadastro:</p>
                            <ul className="mt-1 list-disc list-inside text-amber-700 dark:text-amber-400">
                                {entry.unresolvedPersons.map((name, i) => <li key={i}>{name}</li>)}
                            </ul>
                        </div>
                    )}

                    {receivables && receivables.quantity > 0 && (
                        <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
                            <div className="overflow-x-auto overflow-y-auto max-h-[180px] rounded border border-gray-200 dark:border-gray-700">
                                <table className="w-full text-xs whitespace-nowrap">
                                    <thead className="bg-gray-100 dark:bg-slate-700 sticky top-0">
                                        <tr>
                                            <th className="text-left px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Cedente</th>
                                            <th className="text-left px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">CNPJ Cedente</th>
                                            <th className="text-left px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Sacado</th>
                                            <th className="text-left px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">CNPJ Sacado</th>
                                            <th className="text-right px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Emissão</th>
                                            <th className="text-right px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Vencimento</th>
                                            <th className="text-right px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Valor Venc.</th>
                                            <th className="text-right px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Valor Desemb.</th>
                                            <th className="text-right px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Prazo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {receivables.items.map((item: UnderlyingReceivable, idx: number) => (
                                            <tr key={item.id} className={idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'}>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{item.cedente}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{item.cnpj_cedente}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{item.sacado}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{item.cnpj_sacado}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 text-right">{formatDate(item.emissao)}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 text-right">{formatDate(item.vencimento)}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 text-right">{formatCurrency(item.valor_vencimento)}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 text-right">{formatCurrency(item.valor_desembolso)}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 text-right">{item.prazo_dias}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                                <span>{receivables.quantity} título(s)</span>
                                <span>Total: {formatCurrency(receivables.total_vencimento)} (venc.) / {formatCurrency(receivables.total_desembolso)} (desemb.)</span>
                            </div>
                        </div>
                    )}

                    {/* Base Calculation & Financial Params */}
                    {(payload.operation_dictionary?.base_calculation || payload.operation_dictionary?.financial_params) && (
                        <div className="border-t border-gray-100 dark:border-gray-700 pt-2 grid grid-cols-2 gap-4">
                            {payload.operation_dictionary.base_calculation && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Cálculo Base</p>
                                    <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">TIR a.a.</span>
                                            <p className="text-gray-900 dark:text-gray-100 font-medium">{(Number(payload.operation_dictionary.base_calculation.tir_annual) || 0).toFixed(4)}%</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">TIR a.m.</span>
                                            <p className="text-gray-900 dark:text-gray-100 font-medium">{(Number(payload.operation_dictionary.base_calculation.tir_monthly) || 0).toFixed(4)}%</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Spread a.a.</span>
                                            <p className="text-gray-900 dark:text-gray-100 font-medium">{(Number(payload.operation_dictionary.base_calculation.spread_annual) || 0).toFixed(4)}%</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">VPL</span>
                                            <p className="text-gray-900 dark:text-gray-100 font-medium">{formatCurrency(Number(payload.operation_dictionary.base_calculation.vpl) || 0)}</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Margem VPL</span>
                                            <p className="text-gray-900 dark:text-gray-100 font-medium">{(Number(payload.operation_dictionary.base_calculation.vpl_margin) || 0).toFixed(4)}%</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Duration</span>
                                            <p className="text-gray-900 dark:text-gray-100 font-medium">{Number(payload.operation_dictionary.base_calculation.duration_days) || 0} dias</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {payload.operation_dictionary.financial_params && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Parâmetros Financeiros</p>
                                    <div className="grid grid-cols-4 gap-x-3 gap-y-1 text-xs">
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">CDI (a.a.)</span>
                                            <p className="text-gray-900 dark:text-gray-100 font-medium">{((Number(payload.operation_dictionary?.underlying_receivables?.cdi_aa) || 0) * 100).toFixed(4)}%</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Funding Rate</span>
                                            <p className="text-gray-900 dark:text-gray-100 font-medium">{(Number(payload.operation_dictionary.financial_params.funding_rate) || 0).toFixed(4)}%</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Taxa Plataforma</span>
                                            <p className="text-gray-900 dark:text-gray-100 font-medium">{(Number(payload.operation_dictionary.financial_params.platform_fee_rate) || 0).toFixed(4)}%</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Grossup</span>
                                            <p className="text-gray-900 dark:text-gray-100 font-medium">{(Number(payload.operation_dictionary.financial_params.grossup_tax_rate) || 0).toFixed(4)}%</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Endereço de Cobrança */}
                    {payload.operation_dictionary?.assignor_billing_address && (
                        <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Endereço do Sacado</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-xs">
                                <div className="col-span-2">
                                    <span className="text-gray-500 dark:text-gray-400">Nome</span>
                                    <p className="text-gray-900 dark:text-gray-100 font-medium truncate">{payload.operation_dictionary.assignor_billing_address.name || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500 dark:text-gray-400">Documento</span>
                                    <p className="text-gray-900 dark:text-gray-100 font-medium">{payload.operation_dictionary.assignor_billing_address.document_number || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500 dark:text-gray-400">CEP</span>
                                    <p className="text-gray-900 dark:text-gray-100 font-medium">{payload.operation_dictionary.assignor_billing_address.address_zip_code || '-'}</p>
                                </div>
                                <div className="col-span-2">
                                    <span className="text-gray-500 dark:text-gray-400">Logradouro</span>
                                    <p className="text-gray-900 dark:text-gray-100 font-medium truncate">{payload.operation_dictionary.assignor_billing_address.address_street || '-'}{payload.operation_dictionary.assignor_billing_address.address_number ? `, ${payload.operation_dictionary.assignor_billing_address.address_number}` : ''}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500 dark:text-gray-400">Bairro</span>
                                    <p className="text-gray-900 dark:text-gray-100 font-medium">{payload.operation_dictionary.assignor_billing_address.address_neighborhood || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500 dark:text-gray-400">Cidade/UF</span>
                                    <p className="text-gray-900 dark:text-gray-100 font-medium">{payload.operation_dictionary.assignor_billing_address.address_city || '-'}/{payload.operation_dictionary.assignor_billing_address.address_state || '-'}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    const hasBlockingIssue = entry.unresolvedPersons && entry.unresolvedPersons.length > 0;
    const isReady = status === FileStatus.SUCCESS && !entry.isEnriching && !hasBlockingIssue;

    return (
        <div className={`bg-white dark:bg-slate-800 border rounded-lg shadow-sm overflow-hidden ${hasBlockingIssue ? 'border-amber-300 dark:border-amber-600' : isReady ? 'border-blue-200 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700'}`}>
            {hasBlockingIssue && <div className="h-1.5 bg-amber-400 dark:bg-amber-500" />}
            {isReady && <div className="h-1.5 bg-blue-400 dark:bg-blue-500" />}
            <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        {renderStatusIcon()}
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{file.name}</p>
                            <div className="flex items-center gap-2">
                                <p className="text-xs text-gray-500 dark:text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                                {status === FileStatus.SUCCESS && response?.program && (
                                    <span className="text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">
                                        Risco: {response.program}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <button type="button" onClick={() => onRemove(entry.id)}
                        className="text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-400 p-1" title="Remover">
                        <FaTrash className="text-sm" />
                    </button>
                </div>
                {(status === FileStatus.SUCCESS || status === FileStatus.ERROR || status === FileStatus.UPLOADING) && (
                    <div className="mt-3">{renderContent()}</div>
                )}
            </div>
        </div>
    );
};

export default FileCard;
